import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import firebase from "firebase/compat/app";
import "firebase/compat/auth";
import { firebaseApp, db, callRefreshMyAuthClaims } from "@healthdesk/shared-services";
import { debugLog } from "../../utils/debugLog";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import {
  isValidPhoneNumber,
  formatPhoneNumberIntl,
} from "react-phone-number-input";
import {
  Box,
  Typography,
  Alert,
  Link as MuiLink,
  IconButton,
  styled,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { ArrowBack } from "@mui/icons-material";
import AccountSetupFlow, { ACCOUNT_SETUP_STEPS } from "./AccountSetupFlow";
import { isDevAuthCode, getDevAuthData, DEV_MOCK_PATIENT_CODE, isDevAuthEnvironment } from "config/devAuthCodes";
import { getMockPatientSeedForFirestore } from "../../services/mocks/devMockPatientSeed";
import { resumeClickAudio } from "../../pages/splash/splitflapAudio";

// Styled PhoneInput to look like MUI outlined TextField with floating label
const PhoneInputWrapper = styled(Box)(({ theme, $focused }) => ({
  position: "relative",
  "& .phone-label": {
    position: "absolute",
    left: "14px",
    top: 0,
    transform: "translateY(-50%) scale(0.75)",
    transformOrigin: "left top",
    backgroundColor: theme.palette.background.paper,
    padding: "0 4px",
    color: $focused ? theme.palette.primary.main : "rgba(0, 0, 0, 0.6)",
    fontSize: "1rem",
    pointerEvents: "none",
    zIndex: 1,
  },
}));

const StyledPhoneInput = styled(PhoneInput, {
  shouldForwardProp: (prop) => prop !== "$focused",
})(({ theme, $focused }) => ({
  border: `${$focused ? "2px" : "1px"} solid ${
    $focused ? theme.palette.primary.main : "rgba(0, 0, 0, 0.23)"
  }`,
  borderRadius: "4px",
  padding: $focused ? "15px 13px" : "16px 14px",
  "&:hover": {
    borderColor: $focused ? theme.palette.primary.main : "rgba(0, 0, 0, 0.87)",
  },
  "& .PhoneInputInput": {
    border: "none",
    fontSize: "1rem",
    fontFamily: theme.typography.fontFamily,
    color: theme.palette.text.primary,
    backgroundColor: "transparent",
    padding: 0,
    width: "100%",
    "&:focus": {
      outline: "none",
    },
    "&::placeholder": {
      color: "transparent",
    },
  },
  "& .PhoneInputCountrySelect": {
    border: "none",
    fontSize: "1rem",
    fontFamily: theme.typography.fontFamily,
    backgroundColor: "transparent",
    marginRight: "8px",
    "&:focus": {
      outline: "none",
    },
  },
}));

// Individual digit input for verification code
const CodeInput = styled("input")(({ theme, disabled }) => ({
  width: "48px",
  height: "56px",
  textAlign: "center",
  fontSize: "1.5rem",
  fontFamily: theme.typography.fontFamily,
  border: `1px solid rgba(0, 0, 0, 0.23)`,
  borderRadius: "8px",
  backgroundColor: disabled ? "rgba(0, 0, 0, 0.04)" : "transparent",
  opacity: disabled ? 0.6 : 1,
  "&:focus": {
    outline: "none",
    borderColor: theme.palette.primary.main,
    borderWidth: "2px",
  },
}));

const STEPS = {
  PHONE: 0,
  VERIFY: 1,
  ACCOUNT_SETUP: 2, // Delegates to AccountSetupFlow
};

const PhoneAuthFlow = ({
  onClose,
  onSuccess,
  onStepChange,
  backHandlerRef,
  inDialog = false,
  preselectedRole = null,
  returnTo = null,
  onSignupStart,
  onSuccessStep,
}) => {
  const navigate = useNavigate();
  const [step, setStepInternal] = useState(STEPS.PHONE);
  const [accountSetupStep, setAccountSetupStep] = useState(0);

  // Wrapper to notify parent of step changes
  const setStep = (newStep) => {
    setStepInternal(newStep);
    // Map internal steps to external step numbers for parent
    // PHONE=0, VERIFY=1, ACCOUNT_SETUP starts at 2
    onStepChange?.(newStep);
  };

  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [error, setError] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [phoneFocused, setPhoneFocused] = useState(false);
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [verificationCode, setVerificationCode] = useState([
    "",
    "",
    "",
    "",
    "",
    "",
  ]);
  const [verifying, setVerifying] = useState(false);
  const [verificationComplete, setVerificationComplete] = useState(false);
  const recaptchaVerifierRef = useRef(null);
  const recaptchaContainerRef = useRef(null);
  const codeInputRefs = useRef([]);
  const accountSetupBackRef = useRef(null);

  // Update parent step when account setup step changes
  useEffect(() => {
    if (step === STEPS.ACCOUNT_SETUP) {
      // Account setup steps start at 2 (after PHONE=0, VERIFY=1)
      onStepChange?.(2 + accountSetupStep);
    }
  }, [accountSetupStep, step, onStepChange]);

  const termsHref = "/terms-of-use";
  const privacyHref = "/privacy-policy";

  // Cleanup reCAPTCHA on unmount only
  useEffect(() => {
    return () => {
      if (recaptchaVerifierRef.current) {
        try {
          recaptchaVerifierRef.current.clear();
        } catch (e) {
          // Ignore
        }
        recaptchaVerifierRef.current = null;
      }
    };
  }, []);

  // Get or create reCAPTCHA verifier
  const getRecaptchaVerifier = useCallback(async () => {
    if (recaptchaVerifierRef.current) {
      return recaptchaVerifierRef.current;
    }

    let attempts = 0;
    while (!recaptchaContainerRef.current && attempts < 10) {
      await new Promise((resolve) => setTimeout(resolve, 50));
      attempts++;
    }

    if (!recaptchaContainerRef.current) {
      throw new Error("reCAPTCHA container not found.");
    }

    recaptchaVerifierRef.current = new firebase.auth.RecaptchaVerifier(
      recaptchaContainerRef.current,
      {
        size: "invisible",
        callback: () => {},
        "expired-callback": () => {
          setError("reCAPTCHA expired. Please try again.");
        },
      }
    );

    return recaptchaVerifierRef.current;
  }, []);

  // Handle sending verification code
  const sendVerificationCode = async (e) => {
    e?.preventDefault();
    resumeClickAudio();

    if (!phoneNumber || !isValidPhoneNumber(phoneNumber)) {
      setError("Please enter a valid phone number.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      // Skip real SMS in any dev auth environment (localhost, demo site, experimental hosts)
      // so users can enter dev codes without receiving an SMS.
      if (isDevAuthEnvironment()) {
        setConfirmationResult({
          confirm: async () => {
            // Dev codes are handled in verifyCode(); never call Firebase SMS with a dev code.
            throw new Error('Use dev code path');
          }
        });
        setStep(STEPS.VERIFY);
        setLoading(false);
        return;
      }

      // PRODUCTION: Use real Firebase phone authentication with reCAPTCHA
      const verifier = await getRecaptchaVerifier();

      const result = await firebaseApp
        .auth()
        .signInWithPhoneNumber(phoneNumber, verifier);

      setConfirmationResult(result);
      setStep(STEPS.VERIFY);
    } catch (err) {
      console.error("Error sending verification code:", err);

      if (err.code === "auth/invalid-phone-number") {
        setError("Invalid phone number format.");
      } else if (err.code === "auth/too-many-requests") {
        // Limit is enforced by Firebase (per IP / per number); cannot be raised in app code.
        setError("Too many attempts. Please try again later.");
      } else if (err.code === "auth/invalid-app-credential") {
        setError("Verification failed. Please refresh the page and try again.");
      } else {
        setError(err.message || "Failed to send verification code.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle verification code input
  const handleCodeChange = useCallback(
    (index, value) => {
      if (verifying || verificationComplete) return;
      if (value && !/^\d$/.test(value)) return;

      setVerificationCode((prev) => {
        const newCode = [...prev];
        newCode[index] = value;
        return newCode;
      });

      if (value && index < 5) {
        codeInputRefs.current[index + 1]?.focus();
      }
    },
    [verifying, verificationComplete]
  );

  // Handle backspace in code inputs
  const handleCodeKeyDown = useCallback(
    (index, e) => {
      if (verifying || verificationComplete) return;
      if (e.key === "Backspace" && !verificationCode[index] && index > 0) {
        codeInputRefs.current[index - 1]?.focus();
      }
    },
    [verificationCode, verifying, verificationComplete]
  );

  // Handle paste for verification code
  const handleCodePaste = useCallback(
    (e) => {
      if (verifying || verificationComplete) return;
      e.preventDefault();
      const pastedData = e.clipboardData
        .getData("text")
        .replace(/\D/g, "")
        .slice(0, 6);
      if (pastedData) {
        const newCode = pastedData.split("");
        while (newCode.length < 6) newCode.push("");
        setVerificationCode(newCode);
        const focusIndex = Math.min(pastedData.length, 5);
        codeInputRefs.current[focusIndex]?.focus();
      }
    },
    [verifying, verificationComplete]
  );

  // Auto-submit when 6 digits entered
  useEffect(() => {
    const code = verificationCode.join("");
    if (
      code.length === 6 &&
      confirmationResult &&
      !verifying &&
      !verificationComplete
    ) {
      verifyCode(code);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verificationCode, confirmationResult, verifying, verificationComplete]);

  // Verify the code
  const verifyCode = async (code) => {
    if (verifying || verificationComplete) return;
    resumeClickAudio();

    setVerifying(true);
    setError("");

    try {
      // Check if this is a dev auth code
      if (isDevAuthCode(code)) {
        const devData = getDevAuthData(code);
        
        // Create dev account with email/password auth
        const devPassword = 'devpass123'; // Standard dev password
        let user;
        
        try {
          // Try to sign in with existing dev account
          const result = await firebaseApp.auth().signInWithEmailAndPassword(devData.email, devPassword);
          user = result.user;

          // Ensure Firestore user doc has latest dev role (merge so we create doc if missing)
          const updateData = {
            uid: user.uid,
            email: devData.email,
            role: devData.role,
            phone: phoneNumber || null,
            phoneVerified: true,
            name: devData.name,
            isDevAccount: true,
            devAuthCode: code,
            lastLogin: firebase.firestore.FieldValue.serverTimestamp(),
          };
          if (devData.facilityId) {
            updateData.facilityId = devData.facilityId;
            updateData.clinicId = devData.facilityId;
          }
          try {
            await db.collection("users").doc(user.uid).set(updateData, { merge: true });
          } catch (firestoreError) {
            console.error("[Dev auth] Failed to write user doc (role may be missing for rules):", firestoreError);
            setError(firestoreError?.message?.includes("permission") ? "Permission denied saving your role. Check Firestore rules for users." : "Could not save your profile. Try again.");
            setVerifying(false);
            return;
          }
          // Ensure role is visible so rules (e.g. medications) can allow provider writes
          const afterSnap = await db.collection("users").doc(user.uid).get();
          if (!afterSnap.exists || afterSnap.data()?.role !== devData.role) {
            console.error("[Dev auth] User doc missing or role not set after write");
            setError("Your role was not saved. Please sign out and sign in again with code " + code);
            setVerifying(false);
            return;
          }

          try {
            await callRefreshMyAuthClaims();
            await firebaseApp.auth().currentUser?.getIdToken(true);
          } catch (claimErr) {
            console.warn("[Dev auth] refreshMyAuthClaims:", claimErr);
          }

          if (code === DEV_MOCK_PATIENT_CODE) {
            const seed = getMockPatientSeedForFirestore();
            await db.collection("users").doc(user.uid).set(seed, { merge: true });
            try {
              localStorage.setItem("devMockPatientUid", user.uid);
            } catch (e) {
              // ignore localStorage errors
            }
          }

          if (process.env.NODE_ENV !== "production") {
            debugLog(`[Dev auth] Signed in as ${devData.role} (code ${code})`);
          }
          
        } catch (signInError) {
          // For dev auth codes, create only when the user truly does not exist.
          if (signInError.code === 'auth/user-not-found') {
            try {
              const result = await firebaseApp.auth().createUserWithEmailAndPassword(devData.email, devPassword);
              user = result.user;
            } catch (createError) {
              if (createError?.code === 'auth/email-already-in-use') {
                const retrySignIn = await firebaseApp.auth().signInWithEmailAndPassword(
                  devData.email,
                  devPassword
                );
                user = retrySignIn.user;
              } else {
                throw createError;
              }
            }
            
            // Update display name
            await user.updateProfile({ displayName: devData.name }).catch((profileError) => {
              console.warn('[Dev auth] Failed to update display name:', profileError);
            });
            
            // Create user document with the dev role
            const userData = {
              uid: user.uid,
              phone: phoneNumber || null,
              phoneVerified: true,
              email: devData.email,
              name: devData.name,
              lastName: 'User',
              role: devData.role,
              registrationDate: firebase.firestore.FieldValue.serverTimestamp(),
              onboarding: false,
              isDevAccount: true,
              devAuthCode: code,
            };
            // Add facilityId if specified in dev auth code (for supervisor, facility, admin roles)
            if (devData.facilityId) {
              userData.facilityId = devData.facilityId;
              userData.clinicId = devData.facilityId; // Also set clinicId for compatibility
            }
            try {
              await db.collection("users").doc(user.uid).set(userData);
            } catch (firestoreError) {
              console.error("[Dev auth] Failed to create user doc:", firestoreError);
              setError(firestoreError?.message?.includes("permission") ? "Permission denied creating your profile. Check Firestore rules." : "Could not create your profile. Try again.");
              setVerifying(false);
              return;
            }
            const afterSnap = await db.collection("users").doc(user.uid).get();
            if (!afterSnap.exists || afterSnap.data()?.role !== devData.role) {
              console.error("[Dev auth] User doc missing or role not set after create");
              setError("Your role was not saved. Please try again with code " + code);
              setVerifying(false);
              return;
            }

            try {
              await callRefreshMyAuthClaims();
              await firebaseApp.auth().currentUser?.getIdToken(true);
            } catch (claimErr) {
              console.warn("[Dev auth] refreshMyAuthClaims:", claimErr);
            }

            if (code === DEV_MOCK_PATIENT_CODE) {
              const seed = getMockPatientSeedForFirestore();
              await db.collection("users").doc(user.uid).set(seed, { merge: true });
              try {
                localStorage.setItem("devMockPatientUid", user.uid);
              } catch (e) {
                // ignore localStorage errors
              }
            }
            
          } else if (
            signInError.code === 'auth/wrong-password' ||
            signInError.code === 'auth/invalid-credential'
          ) {
            const fallbackSignIn = await firebaseApp.auth().signInWithEmailAndPassword(
              devData.email,
              devPassword
            );
            user = fallbackSignIn.user;
          } else {
            throw signInError;
          }
        }

        setVerificationComplete(true);
        if (onSuccess) {
          onSuccess(user);
        } else {
          navigate(returnTo || "/dashboard");
        }
        return;
      }

      // Normal SMS verification flow
      if (!confirmationResult) {
        setError("Please request a verification code first.");
        setVerifying(false);
        return;
      }

      const result = await confirmationResult.confirm(code);
      const user = result.user;

      setVerificationComplete(true);

      const isNew = user.metadata.creationTime === user.metadata.lastSignInTime;
      const userDoc = await db.collection("users").doc(user.uid).get();
      const hasProfile = userDoc.exists && userDoc.data()?.role;

      if (isNew || !hasProfile) {
        setStep(STEPS.ACCOUNT_SETUP);
      } else {
        if (onSuccess) {
          onSuccess(user);
        } else {
          navigate(returnTo || "/dashboard");
        }
      }
    } catch (err) {
      console.error("Error verifying code:", err);

      if (err.code === "auth/invalid-verification-code") {
        setError("Invalid verification code. Please try again.");
      } else if (err.code === "auth/code-expired") {
        setError("Verification code expired. Please request a new code.");
      } else if (err.code === "auth/too-many-requests") {
        setError("Too many attempts. Please try again later.");
      } else {
        setError(err.message || "Failed to verify code.");
      }

      setVerificationCode(["", "", "", "", "", ""]);
      codeInputRefs.current[0]?.focus();
    } finally {
      setVerifying(false);
    }
  };

  // Resend verification code
  const resendCode = async () => {
    resumeClickAudio();
    setResendLoading(true);
    setError("");
    setVerificationComplete(false);

    try {
      if (isDevAuthEnvironment()) {
        setVerificationCode(["", "", "", "", "", ""]);
        codeInputRefs.current[0]?.focus();
        setResendLoading(false);
        return;
      }

      // PRODUCTION: Resend real Firebase verification code
      const verifier = await getRecaptchaVerifier();

      const result = await firebaseApp
        .auth()
        .signInWithPhoneNumber(phoneNumber, verifier);

      setConfirmationResult(result);
      setVerificationCode(["", "", "", "", "", ""]);
      codeInputRefs.current[0]?.focus();
    } catch (err) {
      console.error("Error resending code:", err);
      if (err.code === "auth/too-many-requests") {
        setError("Too many attempts. Please try again later.");
      } else {
        setError("Failed to resend code. Please try again.");
      }
    } finally {
      setResendLoading(false);
    }
  };

  // Universal back handler
  const handleBack = async () => {
    if (step === STEPS.VERIFY) {
      setStep(STEPS.PHONE);
      setVerificationCode(["", "", "", "", "", ""]);
      setVerificationComplete(false);
      setConfirmationResult(null);
      setError("");
    } else if (step === STEPS.ACCOUNT_SETUP) {
      // Delegate to AccountSetupFlow's back handler
      if (accountSetupStep === ACCOUNT_SETUP_STEPS.ACCOUNT_TYPE) {
        // On first step of account setup, go back to phone entry and sign out
        try {
          await firebaseApp.auth().signOut();
        } catch (e) {
          // Ignore
        }
        setStep(STEPS.PHONE);
        setPhoneNumber("");
        setVerificationCode(["", "", "", "", "", ""]);
        setConfirmationResult(null);
        setVerificationComplete(false);
        setAccountSetupStep(0);
        setError("");
      } else {
        // Let AccountSetupFlow handle its internal back navigation
        accountSetupBackRef.current?.();
      }
    }
  };

  // Expose handleBack to parent via ref
  useEffect(() => {
    if (backHandlerRef) {
      backHandlerRef.current = handleBack;
    }
  });

  // Handle account setup completion
  const handleAccountSetupComplete = (user) => {
    if (onSuccess) {
      onSuccess(user);
    } else {
      navigate(returnTo || "/dashboard");
    }
  };

  // Format phone for display
  const formattedPhone = phoneNumber ? formatPhoneNumberIntl(phoneNumber) : "";

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: inDialog ? "100%" : 400,
        mx: "auto",
        p: inDialog ? 0 : 3,
        bgcolor: "background.paper",
      }}
    >
      {/* Invisible reCAPTCHA container */}
      <div ref={recaptchaContainerRef} />

      {/* Back button - only show when not on first step, not in dialog, and not on success step */}
      {!inDialog && step !== STEPS.PHONE && accountSetupStep !== ACCOUNT_SETUP_STEPS.SUCCESS && (
        <Box sx={{ mb: 2 }}>
          <IconButton onClick={handleBack} edge="start" sx={{ ml: -1 }}>
            <ArrowBack />
          </IconButton>
        </Box>
      )}

      {/* Step 1: Phone Number */}
      {step === STEPS.PHONE && (
        <form onSubmit={sendVerificationCode}>
          <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>
            Enter your phone number
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
            We'll send you a code to log in or sign up.
          </Typography>

          <PhoneInputWrapper $focused={phoneFocused} sx={{ mb: 3 }}>
            <span className="phone-label" id="phone-auth-field-label">
              Phone number
            </span>
            <StyledPhoneInput
              international
              countryCallingCodeEditable={false}
              defaultCountry="US"
              value={phoneNumber}
              onChange={setPhoneNumber}
              onFocus={() => setPhoneFocused(true)}
              onBlur={() => setPhoneFocused(false)}
              $focused={phoneFocused}
              autoFocus
              numberInputProps={{
                "aria-labelledby": "phone-auth-field-label",
              }}
              countrySelectProps={{
                "aria-label": "Country calling code",
              }}
            />
          </PhoneInputWrapper>

          <LoadingButton
            type="submit"
            variant="contained"
            size="large"
            fullWidth
            loading={loading}
            disabled={!phoneNumber || !isValidPhoneNumber(phoneNumber || "")}
            sx={{ py: 1.5 }}
          >
            {returnTo === "/symptomchecker"
              ? "Continue To Symptom Checker"
              : returnTo === "/chartmind"
                ? "Continue to ChartMind Trial"
                : returnTo === "/getonthemap"
                  ? "Get On the Map"
                  : "Continue with My HealthDesk"}
          </LoadingButton>

          {error && (
            <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError("")}>
              {error}
            </Alert>
          )}

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 3, textAlign: "center", display: "block" }}
          >
            By providing a mobile number, you give MD3C permission to send you a
            one-time verification code. Message and data rates may apply.
            Subject to the MD3C{" "}
            <MuiLink href={termsHref} target="_blank" rel="noreferrer">
              Terms of Service
            </MuiLink>{" "}
            and{" "}
            <MuiLink href={privacyHref} target="_blank" rel="noreferrer">
              Privacy Statement
            </MuiLink>
            .
          </Typography>
        </form>
      )}

      {/* Step 2: Verification Code */}
      {step === STEPS.VERIFY && (
        <>
          <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>
            Confirm it's you
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
            Enter the code sent to {formattedPhone} to securely log in or sign
            up.{" "}
            <MuiLink
              component="button"
              type="button"
              variant="body2"
              onClick={handleBack}
              sx={{ verticalAlign: "baseline" }}
            >
              Change number
            </MuiLink>
          </Typography>

          {/* Dev hint - show when dev codes are accepted (localhost or demo/experimental hosts) */}
          {isDevAuthEnvironment() && (
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="caption">
                🔧 <strong>Dev Mode:</strong> Use development codes provided to authenticate with the desired role.
              </Typography>
            </Alert>
          )}

          <Box
            sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}
            onPaste={handleCodePaste}
          >
            {verificationCode.map((digit, index) => (
              <CodeInput
                key={index}
                ref={(el) => (codeInputRefs.current[index] = el)}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleCodeChange(index, e.target.value)}
                onKeyDown={(e) => handleCodeKeyDown(index, e)}
                autoFocus={index === 0}
                disabled={verifying || verificationComplete}
              />
            ))}
          </Box>

          <LoadingButton
            variant="outlined"
            size="large"
            fullWidth
            loading={verifying || resendLoading}
            onClick={resendCode}
            disabled={verifying}
            sx={{ py: 1.5 }}
          >
            {verifying ? "Verifying..." : "Resend code"}
          </LoadingButton>

          {error && (
            <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError("")}>
              {error}
            </Alert>
          )}

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 3, textAlign: "center", display: "block" }}
          >
            By continuing, your name and email address may be shared with your
            healthcare institution. Subject to the MD3C{" "}
            <MuiLink href={termsHref} target="_blank" rel="noreferrer">
              Terms of Service
            </MuiLink>{" "}
            and{" "}
            <MuiLink href={privacyHref} target="_blank" rel="noreferrer">
              Privacy Statement
            </MuiLink>
            .
          </Typography>
        </>
      )}

      {/* Step 3+: Account Setup (delegates to AccountSetupFlow) */}
      {step === STEPS.ACCOUNT_SETUP && (
        <AccountSetupFlow
          phoneNumber={formattedPhone}
          onComplete={handleAccountSetupComplete}
          onStepChange={setAccountSetupStep}
          backHandlerRef={accountSetupBackRef}
          preselectedRole={preselectedRole}
          onSignupStart={onSignupStart}
          onSuccessStep={onSuccessStep}
          returnTo={returnTo}
        />
      )}
    </Box>
  );
};

export default PhoneAuthFlow;
